/**
 * QA Hub data + accessors.
 *
 * SINGLE SOURCE OF TRUTH: /app/data/qa_hub_entries.json. The same file is
 * read by the backend sitemap endpoint so SEO indexing stays in lock-step
 * with what the frontend renders. Do NOT duplicate the entries here.
 *
 * Schema for each entry:
 *   state             string  AU/QLD/NSW/VIC/SA/WA/TAS/ACT/NT
 *   slug              string  url-safe identifier, unique within state
 *   topic             string  e.g. "Bond", "Repairs", "Notice", "Rent"
 *   title             string  human-readable H1
 *   metaTitle         string  optional override for <title>; falls back to title
 *   metaDescription   string  <=160 chars
 *   shortAnswer       string  bold lead paragraph (used as JSON-LD answer too)
 *   body              string  markdown (rendered with simple paragraph parser)
 *   doNow             array[string]  bullets for "What you can do right now"
 *   ctaContextual     string  one-liner that appears above the CTA buttons
 *   related           array[{label, href}]
 *   freeHelp          array[string]
 */

import data from "./qa_hub_entries.json";

export const QA_HUB_ENTRIES = data.entries || [];

/**
 * Look up one entry by state + slug. Returns null if missing so callers can
 * render a 404. State match is case-insensitive.
 */
export function findQAHubEntry(state, slug) {
  if (!state || !slug) return null;
  const s = state.toUpperCase();
  return QA_HUB_ENTRIES.find((e) => e.state?.toUpperCase() === s && e.slug === slug) || null;
}

/**
 * Group entries by topic for the nav dropdown. Returns
 *   { Bond: [entry, entry], Repairs: [entry, ...], ... }
 * Topic ordering preserved as encountered in the data file.
 */
export function groupQAHubByTopic(entries = QA_HUB_ENTRIES) {
  const groups = {};
  for (const entry of entries) {
    const topic = entry.topic || "General";
    if (!groups[topic]) groups[topic] = [];
    groups[topic].push(entry);
  }
  return groups;
}

/**
 * State + topic groupings used by the index page (if/when we build one).
 * Kept here so any UI surface can reuse the same grouping logic.
 */
export function groupQAHubByState(entries = QA_HUB_ENTRIES) {
  const groups = {};
  for (const entry of entries) {
    const state = entry.state || "AU";
    if (!groups[state]) groups[state] = [];
    groups[state].push(entry);
  }
  return groups;
}
